import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetLocationIsOpenQuery } from '../api/storefrontApi';
import type { RootState } from '../../../types/store';
import { refreshSelectedLocationSchedule } from '../slice/storefrontSlice';
import { humanizeScheduleReason } from '../utils/storeSchedule';

/**
 * Sincroniza `isOpen/todayHours` vía endpoint ligero `/public/location/:id/is-open`
 * con polling. Mantiene el estado humano (sin términos técnicos).
 */
export const useStoreScheduleSync = (opts?: { pollingIntervalMs?: number; enabled?: boolean }) => {
  const { pollingIntervalMs = 2 * 60_000, enabled = true } = opts || {}; // 2min - antes 30s, reduce refrescos
  const dispatch = useDispatch();
  const config = useSelector((s: RootState) => s.storefront.config);
  const selectedLocation = useSelector((s: RootState) => s.storefront.selectedLocation);
  const partnerId = config?.partner?.id;
  const locationId = selectedLocation?.id;

  const skip = !enabled || !partnerId || !locationId;
  const isFirstRef = useRef(true);

  const { data, isFetching } = useGetLocationIsOpenQuery(
    { locationId: locationId!, partnerId: partnerId! },
    {
      skip,
      pollingInterval: pollingIntervalMs,
      refetchOnFocus: false, // evita refetch al volver al tab
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (data && selectedLocation && data.locationId === selectedLocation.id) {
      const friendlyReason = humanizeScheduleReason(data.reason) ?? data.reason;
      const needsUpdate =
        data.isOpen !== selectedLocation.isOpen ||
        friendlyReason !== selectedLocation.scheduleReason;

      if (isFirstRef.current) {
        console.debug('[Storefront polling] schedule inicial', {
          locationId: data.locationId,
          isOpen: data.isOpen,
          at: new Date().toISOString(),
        });
        isFirstRef.current = false;
      } else {
        console.debug('[Storefront polling] schedule refrescado por polling (2min)', {
          locationId: data.locationId,
          isOpen: data.isOpen,
          reason: friendlyReason,
          needsUpdate,
          at: new Date().toISOString(),
        });
      }

      if (needsUpdate) {
        console.debug('[Storefront polling] aplicando cambio de horario en store', {
          isOpen: data.isOpen,
          scheduleReason: friendlyReason,
        });
        dispatch(
          refreshSelectedLocationSchedule({
            isOpen: data.isOpen,
            scheduleReason: friendlyReason,
          })
        );
      }
    }
  }, [data, selectedLocation, dispatch]);

  return { data, isFetching, isEnabled: !skip };
};

export default useStoreScheduleSync;
